import { ArrowLongLeftIcon, ArrowLongRightIcon } from "@heroicons/react/24/outline"
import { classNames } from "../../utils/classNames"

export interface PaginationProps {
  page: number
  pageCount: number
  hasPrevious: boolean
  onPrevious: () => void
  hasNext: boolean
  onNext: () => void
  setPage: (i: number) => void
}

const getVisiblePages = (page: number, pageCount: number, maxVisible: number): Array<number | "…"> => {
  function range(start: number, end: number) {
    return Array.from(Array(end - start + 1), (_, i) => i + start)
  }

  let sideWidth = maxVisible < 9 ? 1 : 2
  let leftWidth = (maxVisible - sideWidth * 2 - 3) >> 1
  let rightWidth = (maxVisible - sideWidth * 2 - 2) >> 1
  if (pageCount <= maxVisible) {
    // no breaks in list
    return range(1, pageCount)
  }
  if (page <= maxVisible - sideWidth - 1 - rightWidth) {
    // no break on left of page
    // return range(1, maxVisible - sideWidth - 1).concat(0, range(pageCount - sideWidth + 1, pageCount))
    return [...range(1, maxVisible - sideWidth - 1), "…", ...range(pageCount - sideWidth + 1, pageCount)]
  }
  if (page >= pageCount - sideWidth - 1 - rightWidth) {
    // no break on right of page
    // return range(1, sideWidth).concat(0, range(pageCount - sideWidth - 1 - rightWidth - leftWidth, pageCount))
    return [
      ...range(1, sideWidth),
      "…",
      ...range(pageCount - sideWidth - 1 - rightWidth - leftWidth, pageCount),
    ]
  }
  // Breaks on both sides
  return [
    ...range(1, sideWidth),
    "…",
    ...range(page - leftWidth, page + rightWidth),
    "…",
    ...range(pageCount - sideWidth + 1, pageCount),
  ]
}

export const Pagination = ({
  page,
  pageCount,
  hasPrevious,
  onPrevious,
  hasNext,
  onNext,
  setPage,
}: PaginationProps) => {
  const visiblePages = getVisiblePages(page, pageCount, 9)

  return (
    <nav className="flex items-center justify-between border-t border-gray-200 px-4 sm:px-0">
      <div className="-mt-px flex w-0 flex-1">
        <button
          onClick={onPrevious}
          disabled={!hasPrevious}
          className="inline-flex items-center border-t-2 border-transparent pt-4 pr-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 disabled:opacity-60 disabled:hover:border-transparent disabled:hover:text-gray-500"
        >
          <ArrowLongLeftIcon className="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
          Previous
        </button>
      </div>
      <div className="hidden md:-mt-px md:flex">
        {visiblePages.map((i, idx) => {
          if (typeof i === "number") {
            return (
              <button
                key={idx}
                onClick={() => setPage(i)}
                className={classNames(
                  "inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium",
                  i === page
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                )}
              >
                {i}
              </button>
            )
          } else {
            return (
              <span
                key={idx}
                className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500"
              >
                ...
              </span>
            )
          }
        })}
      </div>
      <div className="-mt-px flex w-0 flex-1 justify-end">
        <button
          onClick={onNext}
          disabled={!hasNext}
          className="inline-flex items-center border-t-2 border-transparent pt-4 pr-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 disabled:opacity-60 disabled:hover:border-transparent disabled:hover:text-gray-500"
        >
          Next
          <ArrowLongRightIcon className="ml-3 h-5 w-5 text-gray-400" aria-hidden="true" />
        </button>
      </div>
    </nav>
  )
}
